import { useState, useRef, useCallback } from "react";
import { useReconciliation } from "@/contexts/ReconciliationContext";
import { useAccounting } from "@/contexts/AccountingContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, RefreshCw, CheckCircle2, XCircle, AlertTriangle, ArrowRightLeft,
  Trash2, FileSpreadsheet, Link2, Unlink, IndianRupee, TrendingUp, TrendingDown
} from "lucide-react";

const STATUS_STYLES: Record<string, string> = {
  Matched: "bg-info/10 text-info border-info/20",
  Reconciled: "bg-success/10 text-success border-success/20",
  Unmatched: "bg-warning/10 text-warning border-warning/20",
};

const STATUS_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  Matched: ArrowRightLeft,
  Reconciled: CheckCircle2,
  Unmatched: AlertTriangle,
};

function formatINR(val: number) {
  return `₹${val.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

const ReconciliationPage = () => {
  const {
    bankTransactions, summary,
    importFromCSV, importFromExcel, runAutoMatch,
    markReconciled, unmatchTransaction, clearAll,
  } = useReconciliation();
  const { vouchers } = useAccounting();
  const fileRef = useRef<HTMLInputElement>(null);
  const [activeTab, setActiveTab] = useState("all");
  const [manualMatchTxn, setManualMatchTxn] = useState<string | null>(null);
  const { manualMatch } = useReconciliation();

  const handleFileImport = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.name.endsWith(".csv")) {
      const text = await file.text();
      importFromCSV(text);
    } else {
      await importFromExcel(file);
    }
    e.target.value = "";
  }, [importFromCSV, importFromExcel]);

  const filteredTxns = activeTab === "all"
    ? bankTransactions
    : bankTransactions.filter((t) => t.status.toLowerCase() === activeTab);

  const unmatchedVouchers = vouchers.filter(
    (v) => ["Receipt", "Payment", "Contra"].includes(v.type) &&
      v.status !== "Cancelled" &&
      !bankTransactions.some((t) => t.matchedVoucherId === v.id)
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-display text-foreground">Bank Reconciliation</h1>
          <p className="text-sm text-muted-foreground">Import bank statements, match with accounting entries, and reconcile</p>
        </div>
        <div className="flex items-center gap-2">
          {bankTransactions.length > 0 && (
            <>
              <button onClick={runAutoMatch} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors">
                <RefreshCw className="h-4 w-4" /> Auto-Match
              </button>
              <button onClick={clearAll} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-destructive hover:border-destructive/30 transition-colors">
                <Trash2 className="h-4 w-4" /> Clear
              </button>
            </>
          )}
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-accent text-accent-foreground text-sm font-medium hover:bg-accent/90 transition-colors"
          >
            <Upload className="h-4 w-4" /> Import Statement
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileImport} className="hidden" />
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <IndianRupee className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{formatINR(summary.bankBalance)}</p>
              <p className="text-[11px] text-muted-foreground">Bank Balance</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-accent" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{formatINR(summary.ledgerBalance)}</p>
              <p className="text-[11px] text-muted-foreground">Ledger Balance</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`border-border/50 ${summary.difference > 0 ? "ring-1 ring-warning/30" : ""}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-warning/10 flex items-center justify-center">
              <TrendingDown className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="text-lg font-bold text-foreground">{formatINR(summary.difference)}</p>
              <p className="text-[11px] text-muted-foreground">Difference</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[11px] text-muted-foreground">Progress</span>
              <span className="text-xs font-medium text-foreground">
                {summary.totalTransactions > 0 ? Math.round(((summary.matched + summary.reconciled) / summary.totalTransactions) * 100) : 0}%
              </span>
            </div>
            <div className="flex gap-1.5 text-[10px]">
              <div className="flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-success" />{summary.reconciled}
              </div>
              <div className="flex items-center gap-1">
                <ArrowRightLeft className="h-3 w-3 text-info" />{summary.matched}
              </div>
              <div className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3 text-warning" />{summary.unmatched}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Empty State */}
      {bankTransactions.length === 0 && (
        <Card className="border-dashed border-2 border-border">
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <FileSpreadsheet className="h-16 w-16 text-muted-foreground/30" />
            <div className="text-center">
              <h3 className="text-base font-semibold text-foreground mb-1">Import Bank Statement</h3>
              <p className="text-sm text-muted-foreground max-w-md">
                Upload a CSV or Excel file with columns: Date, Description, Reference, Debit/Withdrawal, Credit/Deposit, Balance
              </p>
            </div>
            <button
              onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
            >
              <Upload className="h-4 w-4" /> Upload Statement
            </button>
          </CardContent>
        </Card>
      )}

      {/* Transactions Table */}
      {bankTransactions.length > 0 && (
        <Card className="border-border/50">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Bank Transactions</CardTitle>
            <CardDescription className="text-xs">{summary.totalTransactions} transactions imported</CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-4">
                <TabsTrigger value="all" className="text-xs">All ({summary.totalTransactions})</TabsTrigger>
                <TabsTrigger value="matched" className="text-xs">Matched ({summary.matched})</TabsTrigger>
                <TabsTrigger value="reconciled" className="text-xs">Reconciled ({summary.reconciled})</TabsTrigger>
                <TabsTrigger value="unmatched" className="text-xs">Unmatched ({summary.unmatched})</TabsTrigger>
              </TabsList>

              <div className="rounded-lg border border-border overflow-auto max-h-[500px]">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/30">
                      <TableHead className="text-[11px] font-semibold">Date</TableHead>
                      <TableHead className="text-[11px] font-semibold">Description</TableHead>
                      <TableHead className="text-[11px] font-semibold">Reference</TableHead>
                      <TableHead className="text-[11px] font-semibold text-right">Debit</TableHead>
                      <TableHead className="text-[11px] font-semibold text-right">Credit</TableHead>
                      <TableHead className="text-[11px] font-semibold text-right">Balance</TableHead>
                      <TableHead className="text-[11px] font-semibold text-center">Status</TableHead>
                      <TableHead className="text-[11px] font-semibold text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredTxns.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-sm text-muted-foreground">
                          No transactions in this view
                        </TableCell>
                      </TableRow>
                    ) : filteredTxns.map((txn) => {
                      const StatusIcon = STATUS_ICONS[txn.status] || AlertTriangle;
                      return (
                        <TableRow key={txn.id} className="hover:bg-muted/20">
                          <TableCell className="text-xs whitespace-nowrap">{txn.date}</TableCell>
                          <TableCell className="text-xs max-w-[200px] truncate">{txn.description}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{txn.reference}</TableCell>
                          <TableCell className="text-xs text-right font-mono text-destructive">
                            {txn.debit > 0 ? formatINR(txn.debit) : "—"}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono text-success">
                            {txn.credit > 0 ? formatINR(txn.credit) : "—"}
                          </TableCell>
                          <TableCell className="text-xs text-right font-mono font-medium">{formatINR(txn.balance)}</TableCell>
                          <TableCell className="text-center">
                            <Badge variant="outline" className={`text-[10px] gap-1 ${STATUS_STYLES[txn.status] || ""}`}>
                              <StatusIcon className="h-3 w-3" />
                              {txn.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {txn.status === "Matched" && (
                                <button
                                  onClick={() => markReconciled(txn.id)}
                                  className="p-1.5 rounded-md text-success hover:bg-success/10 transition-colors"
                                  title="Mark Reconciled"
                                >
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {txn.status === "Unmatched" && (
                                <Dialog>
                                  <DialogTrigger asChild>
                                    <button
                                      onClick={() => setManualMatchTxn(txn.id)}
                                      className="p-1.5 rounded-md text-info hover:bg-info/10 transition-colors"
                                      title="Manual Match"
                                    >
                                      <Link2 className="h-3.5 w-3.5" />
                                    </button>
                                  </DialogTrigger>
                                  <DialogContent className="max-w-md">
                                    <DialogHeader>
                                      <DialogTitle className="text-sm">Match Transaction</DialogTitle>
                                    </DialogHeader>
                                    <div className="space-y-3">
                                      <div className="p-3 rounded-lg bg-muted/40 border border-border text-xs">
                                        <p className="font-medium">{txn.description}</p>
                                        <p className="text-muted-foreground mt-1">
                                          {txn.debit > 0 ? `Debit: ${formatINR(txn.debit)}` : `Credit: ${formatINR(txn.credit)}`} — {txn.date}
                                        </p>
                                      </div>
                                      <Select onValueChange={(val) => { manualMatch(txn.id, val); setManualMatchTxn(null); }}>
                                        <SelectTrigger className="text-xs">
                                          <SelectValue placeholder="Select voucher to match" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {unmatchedVouchers.map((v) => (
                                            <SelectItem key={v.id} value={v.id} className="text-xs">
                                              {v.voucherNumber} — {v.type} — {formatINR(v.totalDebit)} — {v.date}
                                            </SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </DialogContent>
                                </Dialog>
                              )}
                              {(txn.status === "Matched" || txn.status === "Reconciled") && (
                                <button
                                  onClick={() => unmatchTransaction(txn.id)}
                                  className="p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
                                  title="Unmatch"
                                >
                                  <Unlink className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReconciliationPage;
